import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Gamepad2, ChevronRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

interface Game {
  id: string;
  name: string;
  cover_url: string;
}

interface GameWithMentors extends Game {
  mentorCount: number;
}

interface GamesSectionProps {
  games: Game[] | undefined;
}

// Modern Game Card Component
const ModernGameCard: React.FC<{ game: GameWithMentors; onClick: () => void }> = ({ game, onClick }) => {
  const [imageError, setImageError] = React.useState(false);
  
  const getFallbackImageUrl = () => {
    const encodedName = encodeURIComponent(game.name);
    return `https://placehold.co/500x280/252B36/00B6F0?text=${encodedName}`;
  };

  return (
    <motion.div
      className="group relative w-80 h-72 bg-gradient-to-br from-[#1A1F2C] to-[#0F1419] rounded-2xl overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl border border-[#223342] hover:border-[#00B6F0]/50 transition-all duration-300"
      onClick={onClick}
      whileHover={{ 
        scale: 1.05,
        y: -8,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        {game.cover_url && !imageError ? (
          <img 
            src={game.cover_url} 
            alt={`Capa de ${game.name}`} 
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            onError={() => setImageError(true)}
          />
        ) : (
          <div 
            className="w-full h-full bg-gradient-to-br from-[#223342] to-[#1A1F2C] flex items-center justify-center"
            style={{
              backgroundImage: `url("${getFallbackImageUrl()}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-black/90 transition-all duration-300" />
      
      {/* Glow Effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute inset-0 bg-gradient-to-t from-[#00B6F0]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#00B6F0]/30 to-transparent" />
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
        <div className="flex items-end justify-between">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#51EFFF] transition-colors">
              {game.name}
            </h3>
            
            {/* Mentor Count */}
            <div className="flex items-center gap-2 text-sm text-gray-300 group-hover:text-[#00B6F0] transition-colors">
              <Users className="h-4 w-4" />
              <span className="font-medium">
                {game.mentorCount} {game.mentorCount === 1 ? 'mentor' : 'mentores'}
              </span>
            </div>
          </div>

          {/* Play Button */}
          <div className="flex-shrink-0 ml-4">
            <div className="w-12 h-12 bg-[#00B6F0] rounded-full flex items-center justify-center group-hover:bg-[#51EFFF] group-hover:shadow-lg group-hover:shadow-[#00B6F0]/50 transition-all duration-300">
              <ChevronRight className="h-6 w-6 text-black ml-0.5" />
            </div>
          </div>
        </div>
      </div>

      {/* Top Badge - Optional for popular games */}
      {game.mentorCount > 5 && (
        <div className="absolute top-4 left-4 bg-gradient-to-r from-[#00B6F0] to-[#51EFFF] text-black text-xs font-bold px-3 py-1 rounded-full z-10">
          Popular
        </div>
      )}
    </motion.div>
  );
};

export const GamesSection: React.FC<GamesSectionProps> = ({ games }) => {
  const navigate = useNavigate();
  
  // Buscar número de mentores para cada jogo através das mentorship_offerings
  const { data: gamesWithMentors } = useQuery({
    queryKey: ["games-with-mentors", games],
    queryFn: async (): Promise<GameWithMentors[]> => {
      if (!games) return [];
      
      const gamesWithMentorCount = await Promise.all(
        games.map(async (game) => {
          // Buscar mentores únicos que têm offerings ativas para este jogo
          const { data, error } = await supabase
            .from("mentorship_offerings")
            .select("mentor_id")
            .eq("game_id", game.id)
            .eq("is_active", true);
          
          if (error) {
            console.error("Error fetching mentor count for game:", game.name, error);
            return { ...game, mentorCount: 0 };
          }
          
          // Contar mentores únicos
          const uniqueMentors = new Set(data?.map(offering => offering.mentor_id) || []);
          const mentorCount = uniqueMentors.size;
          
          console.log(`Game: ${game.name}, Mentors: ${mentorCount}`, uniqueMentors);
          
          return { ...game, mentorCount };
        })
      );
      
      // Ordenar jogos: 1º por número de mentores (decrescente), 2º por nome (alfabética)
      return gamesWithMentorCount.sort((a, b) => {
        // Primeiro critério: número de mentores (decrescente)
        if (b.mentorCount !== a.mentorCount) {
          return b.mentorCount - a.mentorCount;
        }
        // Segundo critério: ordem alfabética (crescente)
        return a.name.localeCompare(b.name, 'pt-BR');
      });
    },
    enabled: !!games && games.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const displayGames = gamesWithMentors || [];
  
  return (
    <section className="py-16 md:py-24 px-4 bg-[#0c1117] relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-5"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#00B6F0] opacity-5 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#8B5CF6] opacity-5 blur-[120px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="p-2 bg-[#00B6F0]/20 rounded-lg">
              <Gamepad2 className="h-8 w-8 text-[#00B6F0]" />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Jogos <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00B6F0] to-[#51EFFF]">Populares</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Descubra os jogos mais procurados e conecte-se com mentores especialistas em cada modalidade
          </p>
        </div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 lg:gap-10 xl:gap-12 justify-items-center" 
          variants={containerVariants} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true, amount: 0.1 }}
        >
          {games ? (
            displayGames.map(game => (
              <motion.div key={game.id} variants={itemVariants}>
                <ModernGameCard 
                  game={game}
                  onClick={() => navigate(`/mentorias?game=${game.id}`)} 
                />
              </motion.div>
            ))
          ) : (
            Array.from({ length: 4 }).map((_, index) => (
              <motion.div 
                key={index} 
                className="w-80 h-72 bg-gradient-to-br from-[#1A1F2C] to-[#0F1419] rounded-2xl animate-pulse border border-[#223342]"
                variants={itemVariants}
              />
            ))
          )}
        </motion.div>

        <div className="mt-16 text-center">
          <Button 
            onClick={() => navigate("/jogos")} 
            size="lg" 
            className="bg-gradient-to-r from-[#00B6F0] to-[#0077B6] text-black font-bold hover:opacity-90 hover:scale-105 transition-all px-8 py-3 text-lg"
          >
            Explorar Todos os Jogos
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};
